import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import { adminCustomers, type AdminCustomer } from "@/mocks/admin";
import CustomerDrawer from "./components/CustomerDrawer";

type StatusFilter = "all" | AdminCustomer["status"];

const statusFilters: { id: StatusFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "new", label: "New" },
  { id: "churned", label: "Churned" },
];

const statusPill: Record<AdminCustomer["status"], string> = {
  active: "bg-primary-100 text-primary-700",
  new: "bg-accent-100 text-accent-700",
  churned: "bg-background-200 text-foreground-600",
};

const statusLabel: Record<AdminCustomer["status"], string> = {
  active: "Active",
  new: "New",
  churned: "Churned",
};

export default function AdminCustomersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [activeId, setActiveId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return adminCustomers.filter((c) => {
      if (filter !== "all" && c.status !== filter) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.city.toLowerCase().includes(q)
      );
    });
  }, [search, filter]);

  const counts = useMemo(
    () => ({
      all: adminCustomers.length,
      active: adminCustomers.filter((c) => c.status === "active").length,
      new: adminCustomers.filter((c) => c.status === "new").length,
      churned: adminCustomers.filter((c) => c.status === "churned").length,
    }),
    [],
  );

  const active = adminCustomers.find((c) => c.id === activeId) || null;

  return (
    <AdminLayout
      title="Customers"
      subtitle={`${adminCustomers.length} total · ${counts.active} active`}
      action={
        <button
          onClick={() => navigate("/booking")}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line"></i>
          New booking
        </button>
      }
    >
      <div className="space-y-4">
        {/* Filter row */}
        <div className="bg-background-50 border border-background-200 rounded-xl p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email or city"
              className="w-full bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-500 rounded-md pl-9 pr-3 py-2 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 overflow-x-auto">
            {statusFilters.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  filter === f.id
                    ? "bg-background-50 text-foreground-950"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {f.label}
                <span className="ml-1.5 text-foreground-500">
                  {counts[f.id as keyof typeof counts]}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-3">
                <i className="ri-user-search-line text-foreground-500 text-xl"></i>
              </span>
              <p className="text-sm font-medium text-foreground-800">No customers match</p>
              <p className="text-xs text-foreground-600 mt-1">
                Try a different search or filter
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-background-200">
              {filtered.map((c) => (
                <li
                  key={c.id}
                  onClick={() => setActiveId(c.id)}
                  className="flex items-center gap-3 md:gap-4 px-4 md:px-5 py-3.5 hover:bg-background-100/60 transition-colors cursor-pointer"
                >
                  <img
                    src={c.avatar}
                    alt={c.name}
                    className="w-11 h-11 md:w-12 md:h-12 rounded-full object-cover object-top flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-foreground-950 truncate">
                        {c.name}
                      </p>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${statusPill[c.status]}`}
                      >
                        {statusLabel[c.status]}
                      </span>
                    </div>
                    <p className="text-xs text-foreground-600 truncate">
                      {c.email} · {c.city}
                    </p>
                  </div>
                  <div className="hidden sm:flex items-center gap-5 text-xs text-foreground-700">
                    <div className="text-right">
                      <p className="font-semibold text-foreground-950">{c.bookings}</p>
                      <p className="text-[10px] text-foreground-500">bookings</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-foreground-950">
                        ${c.lifetime.toLocaleString()}
                      </p>
                      <p className="text-[10px] text-foreground-500">lifetime</p>
                    </div>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-500 text-lg flex-shrink-0"></i>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <CustomerDrawer customer={active} onClose={() => setActiveId(null)} />
    </AdminLayout>
  );
}